import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Loader2, Mail, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
    { value: "app-issue", label: "App Issue" },
    { value: "data-correction", label: "Data Correction" },
    { value: "feature-request", label: "Feature Request" },
    { value: "partnership", label: "Partnership" },
    { value: "other", label: "Other" },
];

const inputCls =
    "bg-[#131520] border-slate-800 text-white placeholder:text-slate-600 focus-visible:border-neon-cyan focus-visible:ring-1 focus-visible:ring-neon-cyan/60 rounded-xl";

const initial = { name: "", email: "", category: "", subject: "", message: "" };

export default function SupportForm() {
    const [form, setForm] = useState(initial);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.category) {
            toast.error("Pick a category", { description: "It helps us route your request faster." });
            return;
        }
        setSending(true);
        try {
            const res = await axios.post(`${API}/support`, form);
            toast.success("Request sent to the pit boss", {
                description: `Ticket ${res.data.ticket_id} — we reply within 1–2 business days.`,
            });
            setForm(initial);
        } catch (err) {
            toast.error("Something jammed the slot machine", {
                description: err.response?.data?.detail || "Please try again in a moment.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="support" className="relative overflow-hidden py-28 sm:py-36" data-testid="support-section">
            <div
                className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-neon-magenta/10 blur-[160px]"
                aria-hidden="true"
            />
            <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon-magenta">
                        03 — Support
                    </p>
                    <h2 className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                        PLACE YOUR <span className="text-neon-magenta neon-magenta">REQUEST</span>
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                        Every submission goes straight to the app developer's inbox — no
                        bots, no queues. Report a bug, correct a restroom listing, or pitch
                        the feature you wish existed at 2AM on Fremont.
                    </p>
                    <div className="mt-10 space-y-5">
                        <div className="flex items-center gap-4">
                            <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-neon-cyan/30 bg-neon-cyan/10">
                                <Clock className="h-4 w-4 text-neon-cyan" />
                            </span>
                            <div>
                                <p className="text-sm font-semibold text-white">Response time</p>
                                <p className="text-sm text-slate-500">Within 1–2 business days</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-neon-magenta/30 bg-neon-magenta/10">
                                <Mail className="h-4 w-4 text-neon-magenta" />
                            </span>
                            <div>
                                <p className="text-sm font-semibold text-white">Direct to the developer</p>
                                <p className="text-sm text-slate-500">Your request becomes a tracked ticket</p>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={onSubmit}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
                    className="glass-panel rounded-2xl p-6 sm:p-8"
                    data-testid="support-form"
                >
                    <div className="grid gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                            <label htmlFor="support-name" className="font-mono text-xs uppercase tracking-widest text-slate-400">
                                Full Name
                            </label>
                            <Input
                                id="support-name"
                                required
                                value={form.name}
                                onChange={set("name")}
                                placeholder="Frank Sinatra"
                                className={inputCls}
                                data-testid="support-form-name-input"
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="support-email" className="font-mono text-xs uppercase tracking-widest text-slate-400">
                                Email
                            </label>
                            <Input
                                id="support-email"
                                type="email"
                                required
                                value={form.email}
                                onChange={set("email")}
                                placeholder="you@example.com"
                                className={inputCls}
                                data-testid="support-form-email-input"
                            />
                        </div>
                    </div>

                    <div className="mt-5 grid gap-5 sm:grid-cols-2">
                        <div className="space-y-2">
                            <label className="font-mono text-xs uppercase tracking-widest text-slate-400">
                                Category
                            </label>
                            <Select
                                value={form.category}
                                onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
                            >
                                <SelectTrigger
                                    className={`${inputCls} ${form.category ? "" : "text-slate-600"}`}
                                    data-testid="support-form-category-select"
                                >
                                    <SelectValue placeholder="Pick a category" />
                                </SelectTrigger>
                                <SelectContent className="border-slate-800 bg-[#131520] text-white">
                                    {CATEGORIES.map((c) => (
                                        <SelectItem
                                            key={c.value}
                                            value={c.value}
                                            className="focus:bg-neon-cyan/15 focus:text-white"
                                            data-testid={`support-category-${c.value}`}
                                        >
                                            {c.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="support-subject" className="font-mono text-xs uppercase tracking-widest text-slate-400">
                                Subject
                            </label>
                            <Input
                                id="support-subject"
                                required
                                value={form.subject}
                                onChange={set("subject")}
                                placeholder="Restroom at The D is closed"
                                className={inputCls}
                                data-testid="support-form-subject-input"
                            />
                        </div>
                    </div>

                    <div className="mt-5 space-y-2">
                        <label htmlFor="support-message" className="font-mono text-xs uppercase tracking-widest text-slate-400">
                            Message
                        </label>
                        <Textarea
                            id="support-message"
                            required
                            rows={5}
                            value={form.message}
                            onChange={set("message")}
                            placeholder="Tell us what happened, which property, and any details that help…"
                            className={`${inputCls} resize-none`}
                            data-testid="support-form-message-textarea"
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={sending}
                        data-testid="support-form-submit-button"
                        className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-neon-magenta via-[#7928CA] to-neon-cyan px-6 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,0,122,0.45)] disabled:opacity-60"
                    >
                        {sending ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Sending…
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4" />
                                Send Support Request
                            </>
                        )}
                    </button>
                </motion.form>
            </div>
        </section>
    );
}
